import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, Search, TrendingUp, Users, Clock, CheckCircle2, Circle, AlertCircle, Edit, Eye, Target, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { projectBoardService } from '../../services/projectBoardService';

import Header from '../../components/ui/Header';


const SuperAdminProjectBoard = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await projectBoardService?.getProjects();
      setProjects(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkScrollButtons = () => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scrollLeft = () => {
    const container = scrollContainerRef?.current;
    if (container) {
      container?.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef?.current;
    if (container) {
      container?.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      on_hold: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      planning: <Circle className="w-4 h-4" />,
      in_progress: <Clock className="w-4 h-4" />,
      completed: <CheckCircle2 className="w-4 h-4" />,
      on_hold: <AlertCircle className="w-4 h-4" />,
      cancelled: <AlertCircle className="w-4 h-4" />
    };
    return icons?.[status] || <Circle className="w-4 h-4" />;
  };

  const formatPhase = (phase) => {
    return phase?.replace(/_/g, ' ')?.toUpperCase() || '';
  };

  const filteredProjects = projects?.filter(project => {
    const matchesPhase = selectedPhase === 'all' || project?.phase === selectedPhase;
    const matchesPriority = selectedPriority === 'all' || project?.priority === selectedPriority;
    const matchesStatus = selectedStatus === 'all' || project?.status === selectedStatus;
    const matchesSearch = !searchQuery || 
      project?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      project?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    return matchesPhase && matchesPriority && matchesStatus && matchesSearch;
  });

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef?.current;
    if (container) {
      container?.addEventListener('scroll', checkScrollButtons);
      return () => container?.removeEventListener('scroll', checkScrollButtons);
    }
  }, [filteredProjects]);

  const calculateStats = () => {
    const total = projects?.length || 0;
    const completed = projects?.filter(p => p?.status === 'completed')?.length || 0;
    const inProgress = projects?.filter(p => p?.status === 'in_progress')?.length || 0;
    const planning = projects?.filter(p => p?.status === 'planning')?.length || 0;
    const avgProgress = total > 0 
      ? Math.round(projects?.reduce((sum, p) => sum + (p?.progress_percentage || 0), 0) / total)
      : 0;

    return { total, completed, inProgress, planning, avgProgress };
  };

  const stats = calculateStats();

  const handleViewProject = async (projectId) => {
    try {
      const projectDetails = await projectBoardService?.getProjectById(projectId);
      setSelectedProject(projectDetails);
      setShowProjectModal(true);
    } catch (err) {
      console.error('Error loading project details:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Project Board</h1>
            <p className="text-gray-600 mt-1">Strategic roadmap and development tracking</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/super-admin-project-board/new')}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Project
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Total Projects</p>
              <p className="text-3xl font-bold text-blue-900 mt-1">{stats?.total}</p>
            </div>
            <BarChart3 className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium">Completed</p>
              <p className="text-3xl font-bold text-green-900 mt-1">{stats?.completed}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-600 text-sm font-medium">In Progress</p>
              <p className="text-3xl font-bold text-yellow-900 mt-1">{stats?.inProgress}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Planning</p>
              <p className="text-3xl font-bold text-purple-900 mt-1">{stats?.planning}</p>
            </div>
            <Circle className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-600 text-sm font-medium">Avg Progress</p>
              <p className="text-3xl font-bold text-indigo-900 mt-1">{stats?.avgProgress}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-indigo-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value || '')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <select
          value={selectedPhase}
          onChange={(e) => setSelectedPhase(e?.target?.value || 'all')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Phases</option>
          <option value="q1_2024">Q1 2024</option>
          <option value="q2_2024">Q2 2024</option>
          <option value="q3_2024">Q3 2024</option>
          <option value="q4_2024">Q4 2024</option>
          <option value="q1_2025">Q1 2025</option>
          <option value="backlog">Backlog</option>
        </select>

        <select
          value={selectedPriority}
          onChange={(e) => setSelectedPriority(e?.target?.value || 'all')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e?.target?.value || 'all')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Statuses</option>
          <option value="planning">Planning</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="on_hold">On Hold</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Project Cards Grid - Scrollable Area with Navigation */}
      <div className="p-6 min-h-[calc(100vh-theme(spacing.64))] relative">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {filteredProjects?.length === 0 ? (
          <div className="text-center py-12">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No projects found</h3>
            <p className="text-gray-600">
              {searchQuery || selectedPhase !== 'all' || selectedPriority !== 'all' || selectedStatus !== 'all' ? 'Try adjusting your filters' : 'Create your first project to get started'}
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Left Scroll Arrow */}
            {showLeftArrow && (
              <button
                onClick={scrollLeft}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors border border-gray-200"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
            )}

            {/* Right Scroll Arrow */}
            {showRightArrow && (
              <button
                onClick={scrollRight}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors border border-gray-200"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-6 h-6 text-gray-700" />
              </button>
            )}

            {/* Scrollable Container */}
            <div
              ref={scrollContainerRef}
              className="flex gap-6 pb-6 px-12 overflow-x-auto overflow-y-visible scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              style={{ scrollbarWidth: 'thin' }}
            >
              {filteredProjects?.map((project) => (
                <div
                  key={project?.id}
                  className="flex-shrink-0 w-80 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => handleViewProject(project?.id)}
                >
                  <div className="p-6">
                    {/* Project Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project?.priority)}`}>
                            {project?.priority?.toUpperCase()}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project?.status)}`}>
                            <span className="inline-flex items-center gap-1">
                              {getStatusIcon(project?.status)}
                              <span>{project?.status?.replace(/_/g, ' ')?.toUpperCase()}</span>
                            </span>
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                          {project?.title}
                        </h3>
                      </div>
                    </div>

                    {/* Project Description */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {project?.description}
                    </p>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700">Progress</span>
                        <span className="text-xs font-bold text-gray-900">{project?.progress_percentage || 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${project?.progress_percentage || 0}%` }}
                        />
                      </div>
                    </div>

                    {/* Project Meta */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatPhase(project?.phase)}</span>
                      </div>
                      {project?.assigned_team && (
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span className="line-clamp-1">{project?.assigned_team}</span>
                        </div>
                      )}
                    </div>

                    {/* Tasks Summary */}
                    {project?.project_tasks?.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-gray-600 mb-4">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>
                          {project?.project_tasks?.filter(t => t?.is_completed)?.length} / {project?.project_tasks?.length} tasks completed
                        </span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleViewProject(project?.id);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          navigate(`/super-admin-project-board/${project?.id}/edit`);
                        }}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminProjectBoard;